import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { connect } from 'react-redux'

import EmptySavedItems from './EmptySavedItems'
import SavedItemsList from './SavedItemsList'
import TripItemFilters from '../TripItemFilters/TripItemFilters'
import CreateTrip from '../TripModal/Trips/CreateTrip/CreateTripMC'

import {
  tripDrawerTripAllSavedItemsView,
  tripDrawerTripPlansView,
  tripPlansCreateNewTripClicked,
} from 'analytics/eventDefinitions'
import {
  fireInteractionEvent,
  fireNonInteractionEvent,
} from 'api/googleTagManager'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineCalendarIcon from 'components/Luxkit/Icons/line/LineCalendarIcon'
import LineHeartIcon from 'components/Luxkit/Icons/line/LineHeartIcon'
import LinePlusCircleIcon from 'components/Luxkit/Icons/line/LinePlusCircleIcon'
import ModalBase from 'components/Luxkit/Modal/ModalBase'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import TabButton from 'components/Luxkit/Tabs/TabButton'
import TabButtonsGroup from 'components/Luxkit/Tabs/TabButtonsGroup'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useNavigateTo from 'hooks/useNavigateTo'
import { useTripItems, useTrips } from 'tripPlanner/hooks/api'
import { useHasTripItems } from 'tripPlanner/hooks/api/tripItem'
import MyTripsEmpty from 'tripPlanner/pages/myTrips/MyTripsEmpty'
import MyTripsListGrid from 'tripPlanner/pages/myTrips/MyTripsListGrid'
import { FullTrip } from 'tripPlanner/types/common'
import { isLeItem } from 'tripPlanner/utils'
import { getTripFilters } from 'tripPlanner/utils/selectors'
import {
  getNonEmptyProductFilters,
  TripPlannerFilterTypeCount,
} from 'tripPlanner/utils/summary'

interface MappedStateProps {
  filters: App.TripPlannerSubFilterType[]
}

function SavedDrawer({ filters }: MappedStateProps) {
  const [selectedTab, setSelectedTab] = useState<'trip-plans' | 'saved-items'>(
    'trip-plans',
  )
  const showModal = useContext(ModalContext)
  const navigateTo = useNavigateTo()
  const modalElement = useModalElementContext()

  const onCreateTrip = useCallback(
    (trip: FullTrip) => {
      navigateTo(`/trip-planner/trip/${trip.id}/summary`)
      modalElement.resolve()
    },
    [modalElement, navigateTo],
  )

  const createNewTrip = useCallback(() => {
    fireInteractionEvent(tripPlansCreateNewTripClicked('trip_drawer'))
    showModal(<CreateTrip onCreateTrip={onCreateTrip} />)
  }, [onCreateTrip, showModal])

  const selectAllSavedItems = useCallback(() => {
    setSelectedTab('saved-items')
  }, [])

  const selectTripPlans = useCallback(() => {
    setSelectedTab('trip-plans')
  }, [])
  const { data: trips, isLoading: isTripsLoading } = useTrips()
  const { data: tripItems, isLoading: isTripItemsLoading } = useTripItems()
  const savedItems = useMemo(
    () => tripItems?.filter((item) => isLeItem(item)) ?? [],
    [tripItems],
  )
  const availableSubFilters: TripPlannerFilterTypeCount[] = useMemo(() => {
    return getNonEmptyProductFilters(savedItems)
  }, [savedItems])

  useEffect(() => {
    if (selectedTab === 'trip-plans') {
      fireNonInteractionEvent(tripDrawerTripPlansView())
    } else if (selectedTab === 'saved-items') {
      fireNonInteractionEvent(tripDrawerTripAllSavedItemsView())
    }
  }, [selectedTab])

  const hasTripItems = useHasTripItems()

  return (
    <ModalBase mode="drawer" size="L">
      <ModalHeader title="Saved">
        <VerticalSpacer gap={8}>
          <TabButtonsGroup>
            <TabButton
              size="large"
              isActive={selectedTab === 'trip-plans'}
              onClick={selectTripPlans}
              startIcon={<LineCalendarIcon />}
            >
              Trip plans
            </TabButton>
            <TabButton
              size="large"
              isActive={selectedTab === 'saved-items'}
              onClick={selectAllSavedItems}
              startIcon={<LineHeartIcon />}
            >
              Saved offers
            </TabButton>
          </TabButtonsGroup>
          {selectedTab === 'saved-items' &&
            savedItems.length > 1 &&
            availableSubFilters.length > 1 && (
              <TripItemFilters
                selectedFilters={filters}
                availableSubFilters={availableSubFilters}
                itemCount={savedItems.length}
                variant="drawer"
                desktopGutterSize={24}
              />
            )}
        </VerticalSpacer>
      </ModalHeader>
      <ModalBody>
        <ModalContent>
          <Group
            direction="vertical"
            gap={selectedTab === 'trip-plans' ? 32 : 8}
          >
            <div />
            {selectedTab === 'trip-plans' && isTripsLoading && (
              <LoadingIndicator inline />
            )}
            {selectedTab === 'saved-items' && isTripItemsLoading && (
              <LoadingIndicator inline />
            )}
            {!isTripsLoading &&
              selectedTab === 'trip-plans' &&
              (!trips || trips.length === 0) && <MyTripsEmpty />}
            {selectedTab === 'trip-plans' && trips && trips.length > 0 && (
              <MyTripsListGrid
                trips={trips}
                source="trip_drawer"
                isVisibleCreateNewTripTile={false}
                className="drawer"
              />
            )}
            {!isTripItemsLoading &&
              selectedTab === 'saved-items' &&
              !hasTripItems && <EmptySavedItems />}
            {!isTripItemsLoading &&
              trips &&
              hasTripItems &&
              selectedTab === 'saved-items' && (
                <SavedItemsList
                  trips={trips}
                  tripItems={savedItems}
                  filters={filters}
                />
              )}
          </Group>
        </ModalContent>
      </ModalBody>
      {selectedTab === 'trip-plans' && (
        <ModalFooter
          primaryActionProps={{
            children: trips && trips.length > 0 ? 'Create new trip' : undefined,
            onClick: createNewTrip,
            startIcon: <LinePlusCircleIcon />,
          }}
        />
      )}
    </ModalBase>
  )
}

export default connect<MappedStateProps, unknown, {}, App.State>((state) => ({
  filters: getTripFilters(state),
}))(SavedDrawer)
